import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function HighlightsForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [sectionId, setSectionId] = useState(36);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editingMode, setEditingMode] = useState(null); // 'add' or 'edit' for existing item
  const [tempNewItem, setTempNewItem] = useState(null); // Temporary item being created (NOT yet in formData)

  // State for media mapping (like HomePage)
  const [mediaMap, setMediaMap] = useState({});

  // Fetch media URLs from API (like HomePage)
  const fetchMediaMap = useCallback(async () => {
    try {
      const response = await fetch(
        `${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache',
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        console.warn('Media fetch failed:', response.status);
        return;
      }

      const result = await response.json();

      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach(media => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
          }
        });
        
        console.log('Media map ready:', Object.keys(map).length, 'items');
        setMediaMap(map);
      }
    } catch (err) {
      console.warn('Media fetch error:', err.message);
    }
  }, [apiBase]);

  // Helper function to build image URL from media_id or filename
  const getImageUrl = (mediaId) => {
    if (!mediaId) return null;
    
    // First check mediaMap (database images)
    if (mediaMap[mediaId]) {
      return mediaMap[mediaId];
    }
    
    const mediaStr = String(mediaId);
    
    // If it's already a full URL
    if (mediaStr.startsWith('http')) {
      return mediaStr;
    }
    
    // If it starts with /, it's already a path
    if (mediaStr.startsWith('/')) {
      return mediaStr;
    }
    
    // If it contains a dot, it's likely a filename
    if (mediaStr.includes('.')) {
      return `/api/uploads/images/${mediaStr}`;
    }
    
    return null;
  };

  // Form header values
  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    intro: '',
    live_label: '',
    live_note: '',
    items: []
  });

  // Pending approvals per field
  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    intro: false,
    live_label: false,
    live_note: false
  });

  // Track original values
  const [originalData, setOriginalData] = useState({...formData});

  // Track item operations (add, update, delete with approval pending)
  const [itemOperations, setItemOperations] = useState({});

  const loadHighlightsSection = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${apiBase}/cms/section/36/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        const { section, pending_changes } = result.data;
        
        if (section && section.content_published) {
          let published = section.content_published;
          
          if (typeof published === 'string') {
            try {
              published = JSON.parse(published);
            } catch (e) {
              published = {};
            }
          }

          const publishedEn = published.en || {};

          const initialData = {
            kicker: publishedEn.kicker || '',
            title: publishedEn.title || '',
            intro: publishedEn.intro || '',
            live_label: publishedEn.live_label || '',
            live_note: publishedEn.live_note || '',
            items: publishedEn.items || []
          };

          setFormData(initialData);
          setOriginalData(initialData);
          setSectionId(section.id);

          // Mark pending fields (only header fields)
          if (Array.isArray(pending_changes)) {
            const pendingMap = {
              kicker: false,
              title: false,
              intro: false,
              live_label: false,
              live_note: false
            };

            const itemOpsMap = {};

            pending_changes.forEach(change => {
              if (change.status === 'pending' && change.locale === 'en') {
                // Header fields
                if (change.field_id in pendingMap) {
                  pendingMap[change.field_id] = true;
                }
                // Item operations
                if (change.field_id && change.field_id.includes('items.')) {
                  const match = change.field_id.match(/items\.(\d+)\.(add|update|delete)/);
                  if (match) {
                    const itemIdx = parseInt(match[1]);
                    itemOpsMap[itemIdx] = { operation: match[2], status: 'pending' };
                  }
                }
              }
            });

            setPendingFields(pendingMap);
            setItemOperations(itemOpsMap);
          }
        }
      }
    } catch (err) {
      console.error('Error loading highlights section:', err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadHighlightsSection();
    fetchMediaMap();
  }, [loadHighlightsSection, fetchMediaMap]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    // If editing new item (in add mode), update tempNewItem
    if (editingMode === 'add') {
      setTempNewItem(prev => ({
        ...prev,
        [field]: value
      }));
    } else {
      // If editing existing item, update formData
      setFormData(prev => ({
        ...prev,
        items: prev.items.map((item, idx) =>
          idx === index ? { ...item, [field]: value } : item
        )
      }));

      // Mark as 'update' operation if item was originally from database
      const originalItem = originalData.items?.[index];
      if (originalItem && !itemOperations[index]) {
        setItemOperations(prev => ({
          ...prev,
          [index]: { operation: 'update', status: 'pending_edit' }
        }));
      }
    }
  };

  const handleAddItem = () => {
    // Create temporary item (NOT added to formData yet)
    const newItem = {
      id: `item_new_${Date.now()}`,
      title: '',
      summary: '',
      media_id: null
    };
    
    // Store in temp state only
    setTempNewItem(newItem);
    
    // Open modal in 'add' mode
    setEditingMode('add');
    setEditingItemIndex(null); // Not referencing formData.items yet
  };

  const handleDeleteItem = (index) => {
    const item = formData.items[index];
    const itemId = item.id || `item_${index}_${Date.now()}`;
    
    if (window.confirm('Delete this highlight item? (Will need approval)')) {
      setItemOperations(prev => ({
        ...prev,
        [index]: { operation: 'delete', status: 'pending_submission' }
      }));
      
      handleSubmitItemOperation(index, 'delete', itemId);
      toast.info('Item marked for deletion (pending approval)');
    }
  };

  const handleImageUpload = async (itemIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Image max 5MB, your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG or WebP allowed');
      return;
    }

    setImageUploading(prev => ({ ...prev, [itemIndex]: true }));
    setImageProgress(prev => ({ ...prev, [itemIndex]: 0 }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setImageProgress(prev => ({ ...prev, [itemIndex]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          if (response.success) {
            // Store filename - use handleItemChange which routes to temp or formData
            handleItemChange(itemIndex, 'media_id', response.filename);
            toast.success('Image uploaded');
          } else {
            toast.error(response.error || 'Upload failed');
          }
        } else {
          toast.error('Upload failed');
        }
        setImageUploading(prev => ({ ...prev, [itemIndex]: false }));
        setImageProgress(prev => ({ ...prev, [itemIndex]: 0 }));
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload error');
        setImageUploading(prev => ({ ...prev, [itemIndex]: false }));
        setImageProgress(prev => ({ ...prev, [itemIndex]: 0 }));
      });

      xhr.open('POST', `${apiBase}/media/upload`);
      xhr.withCredentials = true;
      xhr.send(uploadFormData);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [itemIndex]: false }));
      setImageProgress(prev => ({ ...prev, [itemIndex]: 0 }));
    }
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (formData[fieldName] === originalData[fieldName]) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldName.replace(/_/g, ' '),
            old_value: originalData[fieldName],
            new_value: formData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Submitted for approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));

        setOriginalData(prev => ({
          ...prev,
          [fieldName]: formData[fieldName]
        }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitItemOperation = async (index, operation, itemId) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    // For add mode, use tempNewItem; for edit mode, use formData.items
    const item = editingMode === 'add' ? tempNewItem : formData.items[index];
    
    if (!item && operation !== 'delete') {
      toast.error('Item not found');
      return;
    }

    setSubmitting(prev => ({ ...prev, [`item_${index}_${operation}`]: true }));

    try {
      let fieldId, fieldName, oldValue, newValue;

      if (operation === 'delete') {
        fieldId = `items.${index}.delete`;
        fieldName = `Delete Item: ${item?.title || 'Untitled'}`;
        oldValue = item || null;
        newValue = null;
      } else if (operation === 'add') {
        fieldId = `items.${index}.add`;
        fieldName = `Add Item: ${item.title || 'New Item'}`;
        oldValue = null;
        newValue = item;
      } else if (operation === 'update') {
        fieldId = `items.${index}.update`;
        fieldName = `Update Item: ${item.title || 'Untitled'}`;
        oldValue = originalData.items?.[index] || null;
        newValue = item;
      }

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldId,
            field_name: fieldName,
            old_value: oldValue,
            new_value: newValue,
            locale: 'en',
            item_id: null,
            operation_type: operation
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        // For add operation: add item to formData AFTER successful submission
        if (operation === 'add') {
          const newIndex = formData.items.length;
          setFormData(prev => ({
            ...prev,
            items: [...prev.items, tempNewItem]
          }));
          setItemOperations(prev => ({
            ...prev,
            [newIndex]: { operation: 'add', status: 'pending' }
          }));
          setTempNewItem(null);
          setEditingMode(null);
          setEditingItemIndex(null);
        } else {
          setItemOperations(prev => ({
            ...prev,
            [index]: { operation, status: 'pending' }
          }));
        }

        toast.success(`Item ${operation} submitted for approval`);
      } else {
        if (response.status === 409) {
          toast.error(`Item ${operation} already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [`item_${index}_${operation}`]: false }));
    }
  };

  return (
    <>
      <div className="hero-form-wrapper">
        <ToastContainer />

        <div className="hero-form-header">
          <h2 className="hero-form-title">Highlights Section</h2>
          <p className="hero-form-description">Edit header and manage highlight items (all changes require approval)</p>

          {/* Header Section */}
          <div className="form-section">
            <h3 className="form-section-title">Header Content</h3>

            {/* Kicker */}
            <div className="form-field-card">
              <div className="field-header">
                <label>Kicker / Subtitle</label>
                {pendingFields.kicker && (
                  <span className="status-badge pending">Pending</span>
                )}
              </div>
              <input
                type="text"
                maxLength="50"
                value={formData.kicker}
                onChange={(e) => handleFieldChange('kicker', e.target.value)}
                disabled={submitting.kicker || pendingFields.kicker}
                placeholder="e.g., Highlights"
                className="text-input"
              />
              <button
                onClick={() => handleSubmitField('kicker')}
                disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
                className="submit-btn"
              >
                {submitting.kicker ? 'Submitting...' : 'Submit'}
              </button>
            </div>

            {/* Title */}
            <div className="form-field-card">
              <div className="field-header">
                <label>Section Title</label>
                {pendingFields.title && (
                  <span className="status-badge pending">Pending</span>
                )}
              </div>
              <input
                type="text"
                maxLength="100"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                disabled={submitting.title || pendingFields.title}
                placeholder="e.g., Today on campus"
                className="text-input"
              />
              <button
                onClick={() => handleSubmitField('title')}
                disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
                className="submit-btn"
              >
                {submitting.title ? 'Submitting...' : 'Submit'}
              </button>
            </div>

            {/* Intro */}
            <div className="form-field-card">
              <div className="field-header">
                <label>Introduction</label>
                {pendingFields.intro && (
                  <span className="status-badge pending">Pending</span>
                )}
              </div>
              <textarea
                maxLength="500"
                value={formData.intro}
                onChange={(e) => handleFieldChange('intro', e.target.value)}
                disabled={submitting.intro || pendingFields.intro}
                placeholder="Section introduction..."
                className="textarea-input"
                rows="3"
              />
              <button
                onClick={() => handleSubmitField('intro')}
                disabled={submitting.intro || pendingFields.intro || formData.intro === originalData.intro}
                className="submit-btn"
              >
                {submitting.intro ? 'Submitting...' : 'Submit'}
              </button>
            </div>

            {/* Live Label */}
            <div className="form-field-card">
              <div className="field-header">
                <label>Live Update Label</label>
                {pendingFields.live_label && (
                  <span className="status-badge pending">Pending</span>
                )}
              </div>
              <input
                type="text"
                maxLength="50"
                value={formData.live_label}
                onChange={(e) => handleFieldChange('live_label', e.target.value)}
                disabled={submitting.live_label || pendingFields.live_label}
                placeholder="e.g., Live update"
                className="text-input"
              />
              <button
                onClick={() => handleSubmitField('live_label')}
                disabled={submitting.live_label || pendingFields.live_label || formData.live_label === originalData.live_label}
                className="submit-btn"
              >
                {submitting.live_label ? 'Submitting...' : 'Submit'}
              </button>
            </div>

            {/* Live Note */}
            <div className="form-field-card">
              <div className="field-header">
                <label>Live Update Note</label>
                {pendingFields.live_note && (
                  <span className="status-badge pending">Pending</span>
                )}
              </div>
              <textarea
                maxLength="500"
                value={formData.live_note}
                onChange={(e) => handleFieldChange('live_note', e.target.value)}
                disabled={submitting.live_note || pendingFields.live_note}
                placeholder="Note about live updates..."
                className="textarea-input"
                rows="2"
              />
              <button
                onClick={() => handleSubmitField('live_note')}
                disabled={submitting.live_note || pendingFields.live_note || formData.live_note === originalData.live_note}
                className="submit-btn"
              >
                {submitting.live_note ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>

          {/* Items Section */}
          <div className="form-section">
            <div className="items-section-header">
              <h3 className="form-section-title">Highlight Items</h3>
              <button className="btn-add-item" onClick={handleAddItem}>
                + Add Item
              </button>
            </div>

            {formData.items.length === 0 ? (
              <div className="items-empty-state">
                <div className="empty-icon">No items</div>
                <h4>No items yet</h4>
                <p>Create your first highlight item to get started</p>
              </div>
            ) : (
              <div className="items-list">
                {formData.items.map((item, index) => (
                  <div key={index} className={`items-list-item ${itemOperations[index]?.status === 'pending' ? 'item-pending' : ''}`}>
                    <div className="items-list-image">
                      {item.media_id ? (
                        <img src={getImageUrl(item.media_id)} alt={item.title} onError={(e) => {e.target.src = ''; e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex';}} />
                      ) : null}
                      {!item.media_id ? (
                        <div className="items-list-no-image">No Image</div>
                      ) : null}
                    </div>
                    <div className="items-list-content">
                      <h4 className="items-list-title">{item.title || 'Untitled'}</h4>
                      <p className="items-list-summary">{item.summary || 'No description'}</p>
                    </div>
                    
                  <div className="items-list-actions">
                    {itemOperations[index]?.status === 'pending' && (
                      <span className="items-list-status">Pending</span>
                    )}
                    <button
                      onClick={() => {
                        setEditingMode('edit');
                        setEditingItemIndex(index);
                      }}
                      disabled={itemOperations[index]?.status === 'pending'}
                      className="items-list-btn items-list-btn-edit"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteItem(index)}
                      disabled={itemOperations[index]?.status === 'pending' || itemOperations[index]?.operation === 'delete'}
                      className="items-list-btn items-list-btn-delete"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal - Rendered OUTSIDE wrapper to float above navbar */}
      {(editingItemIndex !== null || editingMode === 'add') && (editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex]) && (
        <div className="edit-modal-overlay" onClick={() => {
          setEditingMode(null);
          setEditingItemIndex(null);
          setTempNewItem(null);
        }}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-new">
              <h3>{editingMode === 'add' ? 'Add New Item' : `Edit: ${(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.title || 'Item'}`}</h3>
              <button className="modal-close-btn" onClick={() => {
                setEditingMode(null);
                setEditingItemIndex(null);
                setTempNewItem(null);
              }}>×</button>
            </div>

            <div className="modal-body">
              {/* Image Preview - Always Show */}
              <div className="modal-image-section">
                <div className="modal-image-preview">
                  {(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.media_id ? (
                    <img src={getImageUrl((editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex]).media_id)} alt={(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.title} onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }} />
                  ) : null}
                  <div className="modal-image-placeholder" style={{display: (editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.media_id ? 'none' : 'flex'}}>
                    <span>No image yet</span>
                  </div>
                </div>
                {(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.media_id && (
                  <p className="modal-image-id">Current: {(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.media_id}</p>
                )}
              </div>

              {/* Title Field */}
              <div className="modal-field-group">
                <label>Title</label>
                <input
                  type="text"
                  value={(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.title || ''}
                  onChange={(e) => handleItemChange(0, 'title', e.target.value)}
                  placeholder="Item title"
                  className="modal-input"
                  maxLength="100"
                />
              </div>

              {/* Summary Field */}
              <div className="modal-field-group">
                <label>Summary</label>
                <textarea
                  value={(editingMode === 'add' ? tempNewItem : formData.items[editingItemIndex])?.summary || ''}
                  onChange={(e) => handleItemChange(0, 'summary', e.target.value)}
                  placeholder="Item description"
                  className="modal-textarea"
                  maxLength="300"
                  rows="3"
                />
              </div>

              {/* Image Upload */}
              <div className="modal-field-group">
                <label>Image</label>
                <div className="modal-upload-area">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleImageUpload(0, e)}
                    disabled={imageUploading[0]}
                    id={`modal-image-0`}
                    className="modal-file-input"
                  />
                  <label htmlFor={`modal-image-0`} className="modal-upload-label">
                    <div className="upload-icon-new">Image</div>
                    <span>{imageUploading[0] ? `${imageProgress[0]}%` : 'Choose Image'}</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="modal-btn-cancel" onClick={() => {
                setEditingMode(null);
                setEditingItemIndex(null);
                setTempNewItem(null);
              }}>
                Cancel
              </button>
              <button
                onClick={() => {
                  const operation = editingMode === 'add' ? 'add' : 'update';
                  handleSubmitItemOperation(editingMode === 'add' ? formData.items.length : editingItemIndex, operation, '');
                }}
                disabled={submitting[`item_0_${editingMode === 'add' ? 'add' : 'update'}`]}
                className="modal-btn-submit"
              >
                {editingMode === 'add' ? 'Save Item' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
